/**
 * Projects API module
 */
import type { Project, ProjectFigmaFile, ProjectSummary } from '@overlayqa/shared';
import { fetchApi, isUuid, buildQuery } from './client';
import { MUTATION_TIMEOUT_MS, SCREEN_READ_TIMEOUT_MS } from './requestTimeout';
import { log } from '@/lib/logger';

log.info('[api/projects] Module loaded');

export interface ProjectMember {
  id: string;
  name: string;
  avatarUrl: string | null;
}

export interface ProjectWithPreview {
  id: string;
  teamId: string;
  name: string;
  slug: string;
  description?: string;
  icon?: string | null;
  iconColor?: string | null;
  figmaFileKey?: string;
  /** The frame a pasted FRAME link named (API form); null for a file link. */
  figmaNodeId?: string | null;
  primaryDomain?: string | null;
  /** Full URL the "Capture Issues" review opens at; null = infer. */
  reviewEntryUrl?: string | null;
  /** Client review link defaults, Sharing tab (migration 0086). */
  clientReviewWelcomeEnabled?: boolean;
  clientReviewWelcomeNote?: string | null;
  clientReviewPrivateComments?: boolean;
  clientReviewAllowResponsive?: boolean;
  createdAt: string;
  updatedAt: string;
  thumbnailUrl?: string | null;
  brandColor?: string | null;
  previewUrl?: string | null;
  statusCounts?: {
    open: number;
    'in-progress': number;
    closed: number;
  };
  ticketCount?: number;
  /**
   * Everyone with access, owner first (list with includeCounts only;
   * composeProjectMembers on the server). Drives the card's avatar stack.
   */
  members?: ProjectMember[];
  /** The calling user's access role for this project. */
  role?: 'owner' | 'collaborator' | 'viewer' | 'team' | null;
  /** Visibility: 'private' (owner/share only) or 'team' (all team members). */
  visibility?: 'private' | 'team';
  /** Demo + scan state (optional; populated for projects returned from listing/get). */
  isDemo?: boolean;
  demoExpiresAt?: string | null;
  scanStatus?: 'queued' | 'scanning' | 'completed' | 'failed' | 'skipped' | null;
  scanUrl?: string | null;
  /** Set when archived. Only ever populated when the list call passed `includeArchived: true`. */
  archivedAt?: string | null;
}

/** Google's public favicon service URL for a domain. */
export function faviconUrl(domain: string, size = 32): string {
  return `https://www.google.com/s2/favicons?domain=${encodeURIComponent(domain)}&sz=${size}`;
}

export const projectsApi = {
  list: (token?: string, teamId?: string, options?: { includePreviews?: boolean; includeCounts?: boolean; includeArchived?: boolean; userId?: string }) => {
    const params: Record<string, string | undefined> = {};
    if (teamId && isUuid(teamId)) {
      params.teamId = teamId;
    }
    if (options?.includePreviews) params.includePreviews = 'true';
    if (options?.includeCounts) params.includeCounts = 'true';
    if (options?.includeArchived) params.includeArchived = 'true';
    const query = buildQuery(params);
    log.info('[api/projects] list', { query });
    // Bounded: ProjectsPage spins on this one request and reaches its LoadError
    // only on rejection, so an unbounded stall is a permanent loader.
    return fetchApi<ProjectWithPreview[]>(`/projects${query}`, {
      token,
      userId: options?.userId,
      timeoutMs: SCREEN_READ_TIMEOUT_MS,
    });
  },

  get: (token: string | undefined, id: string) =>
    fetchApi<ProjectWithPreview>(`/projects/${id}`, { token, timeoutMs: SCREEN_READ_TIMEOUT_MS }),

  create: (token: string | undefined, data: { teamId: string; name: string; slug: string; figmaFileKey?: string; figmaNodeId?: string; primaryDomain?: string; reviewEntryUrl?: string }) =>
    fetchApi<Project>('/projects', {
      token,
      method: 'POST',
      timeoutMs: MUTATION_TIMEOUT_MS,
      body: JSON.stringify(data),
    }),

  update: (token: string | undefined, id: string, data: Partial<{ name: string; description: string; figmaFileKey: string | null; figmaNodeId: string | null; icon: string | null; iconColor: string | null; primaryDomain: string | null; reviewEntryUrl: string | null; visibility: 'private' | 'team'; clientReviewWelcomeEnabled: boolean; clientReviewWelcomeNote: string | null; clientReviewPrivateComments: boolean; clientReviewAllowResponsive: boolean }>) =>
    fetchApi<Project>(`/projects/${id}`, {
      token,
      method: 'PATCH',
      timeoutMs: MUTATION_TIMEOUT_MS,
      body: JSON.stringify(data),
    }),

  delete: (token: string | undefined, id: string) =>
    fetchApi<{ deleted: boolean }>(`/projects/${id}`, {
      token,
      method: 'DELETE',
    }),

  /**
   * Retake the project thumbnail from its URL (Project Settings modal). The
   * server answers a capture that produced no image with 502 CAPTURE_FAILED
   * and a message naming the site, which fetchApi rejects with.
   */
  refreshThumbnail: (token: string | undefined, id: string) =>
    fetchApi<{ thumbnailUrl: string }>(`/projects/${id}/refresh-thumbnail`, {
      token,
      method: 'POST',
    }),

  /**
   * The project's Figma FILE LIST (spec 2026-09-10). Every write answers with
   * the whole list, default first. `figmaFileKey` on the project is a mirror
   * of the default entry and is never written directly by list-aware code.
   */
  figmaFiles: {
    list: (token: string | undefined, projectId: string) =>
      fetchApi<ProjectFigmaFile[]>(`/projects/${projectId}/figma-files`, { token, timeoutMs: SCREEN_READ_TIMEOUT_MS }),
    add: (token: string | undefined, projectId: string, data: { fileKey: string; nodeId?: string | null; name?: string | null; makeDefault?: boolean }) =>
      fetchApi<ProjectFigmaFile[]>(`/projects/${projectId}/figma-files`, {
        token,
        method: 'POST',
        timeoutMs: MUTATION_TIMEOUT_MS,
        body: JSON.stringify(data),
      }),
    setDefault: (token: string | undefined, projectId: string, fileId: string) =>
      fetchApi<ProjectFigmaFile[]>(`/projects/${projectId}/figma-files/${fileId}`, {
        token,
        method: 'PATCH',
        timeoutMs: MUTATION_TIMEOUT_MS,
        body: JSON.stringify({ isDefault: true }),
      }),
    remove: (token: string | undefined, projectId: string, fileId: string) =>
      fetchApi<ProjectFigmaFile[]>(`/projects/${projectId}/figma-files/${fileId}`, {
        token,
        method: 'DELETE',
        timeoutMs: MUTATION_TIMEOUT_MS,
      }),
  },

  getSummary: (token: string | undefined, projectId: string) => {
    log.info('[api/projects] getSummary', { projectId });
    return fetchApi<ProjectSummary>(`/projects/${projectId}/summary`, { token, timeoutMs: SCREEN_READ_TIMEOUT_MS });
  },

  // Demo endpoints
  createSample: (token: string | undefined) =>
    fetchApi<{ project: { id: string; slug: string; demoExpiresAt: string; name: string } }>(
      '/projects/sample',
      { token, method: 'POST', timeoutMs: MUTATION_TIMEOUT_MS },
    ),

  resetDemo: (token: string | undefined, projectId: string) =>
    fetchApi<{ ok: true; ticketCount: number }>(
      `/projects/${projectId}/demo/reset`,
      { token, method: 'POST', timeoutMs: MUTATION_TIMEOUT_MS },
    ),
};
